import { createContext, useContext, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import type { WidgetSettingsDto } from '@/api/generated';

interface WidgetTheme {
  logoImageUrl: string | null;
  backgroundImageUrl: string | null;
  accentColor: string | null;
  hasBackground: boolean;
}

const WidgetThemeContext = createContext<WidgetTheme>({
  logoImageUrl: null,
  backgroundImageUrl: null,
  accentColor: null,
  hasBackground: false,
});

export function useWidgetTheme() {
  return useContext(WidgetThemeContext);
}

function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

interface WidgetThemeProviderProps {
  widgetSettings?: WidgetSettingsDto | null;
  children: ReactNode;
}

export function WidgetThemeProvider({ widgetSettings, children }: WidgetThemeProviderProps) {
  const baseTheme = useTheme();

  const widgetTheme: WidgetTheme = useMemo(() => ({
    logoImageUrl: widgetSettings?.logo_image_url ?? null,
    backgroundImageUrl: widgetSettings?.background_image_url ?? null,
    accentColor: widgetSettings?.accent_color && isValidHexColor(widgetSettings.accent_color)
      ? widgetSettings.accent_color
      : null,
    hasBackground: !!widgetSettings?.background_image_url,
  }), [widgetSettings]);

  const dynamicTheme = useMemo(() => {
    if (!widgetTheme.accentColor) {
      return baseTheme;
    }

    return createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary: {
          main: widgetTheme.accentColor,
        },
      },
    });
  }, [baseTheme, widgetTheme.accentColor]);

  return (
    <WidgetThemeContext.Provider value={widgetTheme}>
      <MuiThemeProvider theme={dynamicTheme}>
        {children}
      </MuiThemeProvider>
    </WidgetThemeContext.Provider>
  );
}
